import { action, computed, makeObservable, observable } from 'mobx'

import {
  IPermitTypeFieldDefaultOptions,
  IPermitTypeFieldDefaultOptionsInput,
} from '~/client/graph'
import {
  WorkflowGroupingKey,
  WorkflowSortingKey,
} from '~/client/src/shared/components/WorkflowList'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import {
  fromDefaultOptions,
  toDefaultOptionsInput,
} from '~/client/src/shared/utils/linkedWorkflowDefaults.utils'

export type PopupType = 'sort' | 'groupBy' | 'filters' | null

export default class LinkedWorkflowDefaultsStore {
  @observable public sortKey: WorkflowSortingKey | null = null
  @observable public sortOrder: SortOrder = SortOrder.ASC
  @observable public groupByKey: WorkflowGroupingKey | null = null
  @observable public filterTagIds: string[] = []
  @observable public activePopup: PopupType = null

  private onChange:
    | ((options: IPermitTypeFieldDefaultOptionsInput) => void)
    | null = null
  private tagsStore: TagsStore | null = null
  private projectsStore: ProjectsStore | null = null
  private workflowTypesStore: WorkflowTypesStore | null = null

  public constructor() {
    makeObservable(this)
  }

  @computed
  public get hasSortDefaults(): boolean {
    return this.sortKey !== null
  }

  @computed
  public get hasGroupByDefaults(): boolean {
    return this.groupByKey !== null
  }

  @computed
  public get hasFilterDefaults(): boolean {
    return this.filterTagIds.length > 0
  }

  @action.bound
  public init(
    defaultOptions: IPermitTypeFieldDefaultOptions | null | undefined,
    onChange: (options: IPermitTypeFieldDefaultOptionsInput) => void,
    tagsStore: TagsStore,
    projectsStore: ProjectsStore,
    workflowTypesStore: WorkflowTypesStore,
  ): void {
    const local = fromDefaultOptions(defaultOptions, workflowTypesStore)
    this.sortKey = local.sortKey
    this.sortOrder = local.sortOrder
    this.groupByKey = local.groupByKey
    this.filterTagIds = local.filterTagIds
    this.onChange = onChange
    this.tagsStore = tagsStore
    this.projectsStore = projectsStore
    this.workflowTypesStore = workflowTypesStore
  }

  @action.bound
  public setSortDefaults(key: WorkflowSortingKey, order: SortOrder): void {
    this.sortKey = key
    this.sortOrder = order
    this.notifyChange()
  }

  @action.bound
  public setGroupByDefault(key: WorkflowGroupingKey | null): void {
    this.groupByKey = key
    this.notifyChange()
  }

  @action.bound
  public setFilterDefaults(tagIds: string[]): void {
    this.filterTagIds = tagIds
    this.notifyChange()
  }

  @action.bound
  public openPopup(popup: PopupType): void {
    this.activePopup = popup
  }

  @action.bound
  public closePopup(): void {
    this.activePopup = null
  }

  @action.bound
  public reset(): void {
    this.sortKey = null
    this.sortOrder = SortOrder.ASC
    this.groupByKey = null
    this.filterTagIds = []
    this.notifyChange()
  }

  private notifyChange(): void {
    if (this.onChange && this.tagsStore) {
      const input = toDefaultOptionsInput(
        {
          sortKey: this.sortKey,
          sortOrder: this.sortOrder,
          groupByKey: this.groupByKey,
          filterTagIds: this.filterTagIds,
        },
        this.tagsStore,
        this.projectsStore ?? undefined,
        this.workflowTypesStore ?? undefined,
      )
      this.onChange(input)
    }
  }
}
